// Inline SVG icon set lifted from Themes/theme1/project/crm-shell.jsx.
// Single source of truth so every CRM screen pulls icons from one place.

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const baseProps: IconProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const small: IconProps = { ...baseProps, width: 14, height: 14 };

export const Ic = {
  dashboard: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  ),
  doc: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  user: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  ),
  briefcase: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  tag: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z" />
      <circle cx="7" cy="7" r="1.4" />
    </svg>
  ),
  coin: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.5 9.5c-.6-1-1.7-1.5-2.5-1.5-1.5 0-3 .8-3 2.3 0 1.5 1.5 2.2 3 2.5 1.5.3 3 1 3 2.5s-1.5 2.2-3 2.2c-1 0-2.2-.5-2.8-1.5M12 6.5v11" />
    </svg>
  ),
  gear: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1A2 2 0 1 1 7 4.3l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  ),
  users: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2 21c0-3.5 3-6.5 7-6.5s7 3 7 6.5" />
      <circle cx="17" cy="6" r="3" />
      <path d="M22 18c0-2.6-2-4.7-4.7-5.3" />
    </svg>
  ),
  // The Accounts rail entry. NOT `users`/`user` — those two are near-identical
  // at the 16px the rail falls back to, and the rail spent both of them: two
  // people on Accounts, one person on Users. An account here is also a record
  // rather than a person (RESIDENTIAL or SMALL_COMMERCIAL, carrying meters and
  // contracts), so a card reads truer than a headcount and stays neutral about
  // which category it is — a building would have picked the commercial side.
  idCard: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      <circle cx="8.5" cy="10.5" r="2.2" />
      <path d="M5 16.4c.6-1.7 2-2.5 3.5-2.5s2.9.8 3.5 2.5" />
      <path d="M15.5 10.5h3.5M15.5 14h3.5" />
    </svg>
  ),
  // The Billing rail entry. NOT `coin` — a circled currency mark promises
  // "money" in the abstract, which is the promise `tag` already makes one group
  // below on Pricing; `coin` also carries four other surfaces (the billing
  // snapshot card, the wizard's payment step, the pricing breakdown), so the
  // rail was borrowing a glyph rather than owning one. Billing is bills, cycles
  // and exceptions, so this is an invoice: the torn lower edge is what separates
  // it from `doc` on Quotes, and it is the one detail that survives 16px.
  receipt: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M5 3h14v18l-3.5-2-3.5 2-3.5-2L5 21z" />
      <path d="M8.5 8.5h7M8.5 12.5h7M8.5 16h4" />
    </svg>
  ),
  bolt: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
    </svg>
  ),
  reports: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 14l3-3 3 3 5-5" />
    </svg>
  ),
  /** Open elsewhere: a frame with an arrow leaving it. `share` (three linked
   *  nodes) means "send this to someone", which is a different promise. */
  external: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </svg>
  ),
  share: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 10.5 15.4 6.5M8.6 13.5l6.8 4" />
    </svg>
  ),
  search: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  ),
  filter: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M4 5h16M7 12h10M10 19h4" />
    </svg>
  ),
  close: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  eye: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  pencil: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  trash: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  ),
  pause: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  ),
  play: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M7 4v16l13-8Z" />
    </svg>
  ),
  bell: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8M10 21a2 2 0 0 0 4 0" />
    </svg>
  ),
  mail: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  ),
  chev: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...small} {...p}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  chevR: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...small} {...p}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  ),
  chevL: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...small} {...p}>
      <path d="m15 6-6 6 6 6" />
    </svg>
  ),
  plus: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p} width={p.width ?? 16} height={p.height ?? 16}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  download: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p} width={p.width ?? 16} height={p.height ?? 16}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  ),
  cal: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 11h18" />
    </svg>
  ),
  refresh: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.3L3 16M3 21v-5h5" />
    </svg>
  ),
  alert: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  flame: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M12 2s4 4 4 9-2 6-4 6-4-1-4-6c0-2 1-3 1-3s2 1 3 0c1-1 0-3 0-4S12 2 12 2zM6 14c0 4 2.7 7 6 7s6-3 6-7" />
    </svg>
  ),
  pulse: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M3 12h4l2-7 4 14 2-7h6" />
    </svg>
  ),
  check: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p} strokeWidth={2.4} width={p.width ?? 11} height={p.height ?? 11}>
      <path d="m5 12 5 5L20 7" />
    </svg>
  ),
  check2: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p} strokeWidth={2} width={p.width ?? 16} height={p.height ?? 16}>
      <path d="m2 13 4 4 8-8M9 13l4 4 8-8" />
    </svg>
  ),
  star: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...small} {...p}>
      <path d="m12 3 3 6 6.5 1-5 4.5 1.5 7-6-3.5L6 21l1.5-7-5-4.5L9 9z" />
    </svg>
  ),
  lock: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p} width={p.width ?? 13} height={p.height ?? 13}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  ),
  more: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p} width={p.width ?? 16} height={p.height ?? 16}>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  ),
  home: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M3 11 12 3l9 8M5 10v10h14V10" />
    </svg>
  ),
  logout: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  ),
  copy: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p} width={p.width ?? 14} height={p.height ?? 14}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  help: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M12 6.5A3 3 0 0 1 18 6.5V19a3 3 0 0 0-6 0z" />
      <path d="M12 6.5A3 3 0 0 0 6 6.5V19a3 3 0 0 1 6 0z" />
      <path d="M12 6.5V19" />
    </svg>
  ),
  docs: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M4 5a2 2 0 0 1 2-2h8l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
      <path d="M14 3v4h4" />
      <path d="M8 12h6M8 16h6" />
    </svg>
  ),
  // FE-CRM-04 — there is deliberately NO `sparkle` glyph here any more.
  //
  // One lived here from FE-CRM-04 until 2026-09-25 as the "Rheora AI" identity:
  // a four-point star, added because this set had no AI/bot/message glyph to
  // reuse. All four of its sites now render the real brand mark instead
  // (`components/crm/RheoraMark`), so it had no consumers left and was removed
  // rather than left in a set whose whole point is to be what the CRM actually
  // draws. Checked across the repo before deleting: `AiTurnRow` held the last
  // one.
  //
  // If a future surface wants an "AI" glyph, the question to ask first is
  // whether it can carry the mark at >=18px on a ground that is not
  // tenant-derived — see the ground table on `.crm-ai-mark` in `styles/crm.css`,
  // and note the AI panel now runs UNPLATED and fails 3:1 on light themes by
  // operator decision, so it is not a precedent to copy. Reviving a
  // stroke star is the fallback for sizes the artwork cannot survive, not the
  // default.
  // FE-CRM-04 — the assistant panel's widen / narrow toggle
  // (--crm-ai-w ⇄ --crm-ai-w-wide).
  //
  // A frame with a docked right-hand panel plus a horizontal arrow: the frame
  // says WHICH region moves and the arrow says it resizes rather than closes,
  // which a bare chevron would not distinguish from dismissing the panel. The
  // caller flips the arrow (or swaps the label) for the narrow direction; one
  // glyph rather than two keeps the button from changing shape mid-interaction.
  panelWide: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M14 4v16" />
      <path d="M6 12h5" />
      <path d="M9 9.5 6.5 12 9 14.5" />
    </svg>
  ),
  // FE-CRM-04 — the assistant composer's send control.
  //
  // An up arrow, not a paper plane. The button sits INSIDE the composer box at
  // its bottom-right corner, so the gesture it names is "push this up into the
  // transcript above" — which is literally the direction the question travels on
  // screen. A plane would also be the only glyph in this set drawn as an object
  // rather than as a stroke, and it would read as "email" beside `Ic.mail`.
  //
  // Head and shaft are one path so the two strokes join at the apex instead of
  // meeting as two mitred ends, which is what stops the tip closing into a blob
  // at the 16px the `sm` IconButton renders it at.
  arrowUp: (p: IconProps = {}) => (
    <svg viewBox="0 0 24 24" {...baseProps} {...p}>
      <path d="M12 19.5V5.5m0 0L5.5 12M12 5.5l6.5 6.5" />
    </svg>
  ),
};
